import { useMemo } from 'react'
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../api/client'
import { useQuery } from '../api/hooks'
import type { QueryState } from '../api/hooks'
import type {
  HealthResponse,
  HoldingsResponse,
  PerformanceSummary,
  PortfolioState,
  RealBrokerResponse,
  UniverseSnapshot,
} from '../api/types'
import { parsePositions } from '../api/types'
import { Badge } from '../components/ui/Badge'
import { ExternalLink } from '../components/ui/ExternalLink'
import { Panel } from '../components/ui/Panel'
import { StatTile } from '../components/ui/StatTile'
import { EmptyState, ErrorState, QueryView, Spinner } from '../components/ui/States'
import { Countdown } from '../components/ui/Countdown'
import {
  CHAIN_HISTORY_START,
  NFT,
  X_HANDLE,
  LINKS,
  explorerAddressUrl,
  explorerTokenUrl,
} from '../config'
import {
  fmtDate,
  fmtSignedPct,
  fmtTime,
  fmtUsd,
  shortAddr,
  signColor,
} from '../lib/format'
import { buildTokenIndex, symbolFor } from '../lib/tokens'

const DONUT_COLORS = [
  '#cfff04',
  '#ff6600',
  '#9e9e9e',
  '#00ff00',
  '#7a9903',
  '#b34700',
  '#6b6b6b',
  '#474546',
]

export function Overview() {
  const health = useQuery(() => api.health())
  const portfolio = useQuery(() => api.portfolioCurrent())
  const perf = useQuery(() => api.performance())
  const universe = useQuery(() => api.universeLatest())
  const realBroker = useQuery(() => api.realBroker())
  const holdings = useQuery(() => api.holdings())

  return (
    <div className="space-y-6">
      <Countdown />

      {/* Agentic Stonker #1 — OUR live on-chain holdings */}
      <Panel title="agentic stonker #1 // holdings">
        <HoldingsCard state={holdings.state} onRetry={holdings.refresh} />
      </Panel>

      {/* Intro + NFT card */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="agent" className="lg:col-span-2">
          <h1 className="text-xl text-crt-lime">
            AGENTIC STONKER<span className="cursor-blink" />
          </h1>
          <p className="mt-3 text-sm leading-relaxed">
            An autonomous portfolio agent living in a token-bound account
            (ERC-6551) on Robinhood Chain. It screens tokenized stocks,
            scores momentum, sizes positions with a mean-variance objective,
            and rebalances on-chain — with hard risk limits, an operator kill
            switch, and a public paper trail for every trade, published to{' '}
            <ExternalLink href={LINKS.xProfile}>{X_HANDLE}</ExternalLink>.
          </p>
          <p className="mt-2 text-xs text-crt-dim">
            Every statistic here is computed over short, adaptive windows
            and should be read accordingly.
          </p>
          <div className="mt-3">
            <GatewayStatus health={health} />
          </div>
        </Panel>

        <Panel title="bound nft">
          <div className="space-y-3">
            <div className="flex items-center justify-center border border-crt-dim/60 bg-crt-bg p-2">
              <img
                src={NFT.imagePath}
                alt={`Agentic Stonker #${NFT.tokenId}`}
                className="h-40 w-40"
                style={{ imageRendering: 'pixelated' }}
              />
            </div>
            <dl className="space-y-1 text-xs">
              <div className="flex justify-between">
                <dt className="text-crt-dim">token id</dt>
                <dd className="text-crt-lime">#{NFT.tokenId}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-crt-dim">collection</dt>
                <dd>
                  <ExternalLink href={explorerTokenUrl(NFT.collection)}>
                    {shortAddr(NFT.collection)}
                  </ExternalLink>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-crt-dim">tba</dt>
                <dd>
                  {NFT.tbaAddress ? (
                    <ExternalLink href={explorerAddressUrl(NFT.tbaAddress)}>
                      {shortAddr(NFT.tbaAddress)}
                    </ExternalLink>
                  ) : (
                    <span
                      className="text-crt-dim"
                      title="The ERC-6551 token-bound account has not been created on-chain yet."
                    >
                      not yet bound
                    </span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-crt-dim">metadata</dt>
                <dd>
                  <ExternalLink href={NFT.metadataPath}>
                    {NFT.metadataPath}
                  </ExternalLink>
                </dd>
              </div>
            </dl>
          </div>
        </Panel>
      </div>

      {/* Real StonkBroker (mainnet) — read-only view of the real deployment */}
      <Panel title="real stonkbroker // mainnet">
        <RealBrokerCard
          state={realBroker.state}
          onRetry={realBroker.refresh}
        />
      </Panel>

      {/* NAV tiles — derived from /portfolio/current + /performance
          (the gateway serves no dedicated /nav endpoint). */}
      <QueryView state={portfolio.state} onRetry={portfolio.refresh}>
        {(p) => (
          <QueryView state={perf.state} onRetry={perf.refresh}>
            {(pf) => <NavTiles state={p.state} performance={pf.performance} />}
          </QueryView>
        )}
      </QueryView>

      {/* Donut + performance */}
      <div className="grid gap-6 lg:grid-cols-5">
        <Panel title="portfolio weights" className="lg:col-span-2">
          <QueryView state={portfolio.state} onRetry={portfolio.refresh}>
            {(p) =>
              p.state ? (
                <WeightsDonut
                  state={p.state}
                  universe={
                    universe.state.kind === 'ready'
                      ? universe.state.data.snapshot
                      : null
                  }
                />
              ) : (
                <EmptyState
                  title="no portfolio state"
                  detail="No portfolio state recorded yet — the agent has not completed a cycle."
                />
              )
            }
          </QueryView>
        </Panel>

        <Panel title="nav performance" className="lg:col-span-3">
          <QueryView state={perf.state} onRetry={perf.refresh}>
            {(pf) =>
              !pf.performance || pf.performance.points.length < 2 ? (
                <EmptyState
                  title="no data yet"
                  detail={`Chain history begins ${CHAIN_HISTORY_START}. The performance series starts after the agent's first completed cycle.`}
                />
              ) : (
                <PerfChart performance={pf.performance} />
              )
            }
          </QueryView>
        </Panel>
      </div>
    </div>
  )
}

function HoldingsCard({
  state,
  onRetry,
}: {
  state: QueryState<HoldingsResponse>
  onRetry: () => void
}) {
  if (state.kind === 'loading') return <Spinner label="reading holdings" />
  if (state.kind === 'error') {
    return (
      <div className="space-y-2">
        <ErrorState message={`holdings unavailable — ${state.message}`} />
        <button className="text-xs text-crt-lime underline" onClick={onRetry}>
          retry
        </button>
      </div>
    )
  }
  const d = state.data
  const held = d.holdings.filter((h) => Number(h.balance) > 0)
  if (held.length === 0) {
    return (
      <EmptyState
        title="no holdings yet"
        detail="The token-bound wallet is empty — the agent has not traded yet."
      />
    )
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <div>
          <span className="text-2xl text-crt-lime">{fmtUsd(d.navUsd)}</span>
          <span className="ml-2 text-xs text-crt-dim">portfolio value</span>
        </div>
        <div className="text-xs text-crt-dim">
          wallet{' '}
          <ExternalLink href={explorerAddressUrl(d.wallet)}>
            {shortAddr(d.wallet)}
          </ExternalLink>{' '}
          · owner{' '}
          <ExternalLink href={explorerAddressUrl(d.holder)}>
            {shortAddr(d.holder)}
          </ExternalLink>
        </div>
      </div>
      <div>
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr>
              <th className="th w-20">token</th>
              <th className="th">balance</th>
              <th className="th w-28 text-right">value</th>
              <th className="th w-24 text-right">weight</th>
            </tr>
          </thead>
          <tbody>
            {held.map((h) => (
              <tr key={h.address} className="hover:bg-crt-panel2">
                <td className="td text-crt-lime">
                  <ExternalLink href={explorerTokenUrl(h.address)}>
                    {h.symbol}
                  </ExternalLink>
                </td>
                <td className="td truncate text-xs text-crt-gray">{h.balance}</td>
                <td className="td text-right text-xs">
                  {h.valueUsd !== null ? fmtUsd(h.valueUsd) : '—'}
                </td>
                <td className="td text-right text-xs text-crt-dim">
                  {h.valueUsd !== null && d.navUsd > 0
                    ? `${((h.valueUsd / d.navUsd) * 100).toFixed(1)}%`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-crt-dim">
        Live on-chain balances of Agentic Stonker #1's ERC-6551 token-bound
        wallet on Robinhood Chain (4663). Prices via Birdeye.
      </p>
    </div>
  )
}

function RealBrokerCard({
  state,
  onRetry,
}: {
  state: QueryState<RealBrokerResponse>
  onRetry: () => void
}) {
  if (state.kind === 'loading') {
    return <Spinner label="reading mainnet" />
  }
  if (state.kind === 'error') {
    // 404 = gateway without the endpoint; 502 = mainnet RPC unreachable.
    // Either way: no data — say so, never fabricate a snapshot.
    return (
      <div className="space-y-2">
        <ErrorState
          message={`real broker view unavailable — ${state.message}`}
          onRetry={onRetry}
        />
        <p className="text-xs text-crt-dim">
          This card is a read-only view of the real deployed StonkBrokers
          contracts on chain 4663; nothing is shown until the gateway can
          read mainnet.
        </p>
      </div>
    )
  }
  const rb = state.data
  const compatOk = rb.compat.ownerCallOk && rb.compat.strangerCallReverts
  return (
    <div className="space-y-3">
      <dl className="space-y-1 text-xs">
        <div className="flex justify-between">
          <dt className="text-crt-dim">broker</dt>
          <dd className="text-crt-lime">#{rb.tokenId}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-crt-dim">collection</dt>
          <dd>
            <ExternalLink href={explorerTokenUrl(rb.nftContract)}>
              {shortAddr(rb.nftContract)}
            </ExternalLink>
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-crt-dim">wallet (tba)</dt>
          <dd>
            <ExternalLink href={explorerAddressUrl(rb.wallet)}>
              {shortAddr(rb.wallet)}
            </ExternalLink>
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-crt-dim">holder</dt>
          <dd>
            <ExternalLink href={explorerAddressUrl(rb.holder)}>
              {shortAddr(rb.holder)}
            </ExternalLink>
          </dd>
        </div>
      </dl>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-crt-dim/40 text-left text-crt-dim">
              <th className="py-1 pr-4 font-normal uppercase tracking-wider">
                token
              </th>
              <th className="py-1 pr-4 text-right font-normal uppercase tracking-wider">
                balance
              </th>
              <th className="py-1 font-normal uppercase tracking-wider">
                address
              </th>
            </tr>
          </thead>
          <tbody>
            {rb.holdings.map((h) => (
              <tr key={h.address} className="border-b border-crt-dim/20">
                <td className="py-1 pr-4 text-crt-lime">{h.symbol}</td>
                <td className="py-1 pr-4 text-right text-crt-gray">
                  {h.balance}
                </td>
                <td className="py-1">
                  <ExternalLink href={explorerTokenUrl(h.address)}>
                    {shortAddr(h.address)}
                  </ExternalLink>
                </td>
              </tr>
            ))}
            <tr>
              <td className="py-1 pr-4 text-crt-lime">ETH</td>
              <td className="py-1 pr-4 text-right text-crt-gray">
                {rb.eth ? rb.eth.balance : '—'}
              </td>
              <td className="py-1 text-crt-dim">native</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge tone={compatOk ? 'pos' : 'neg'}>
          {compatOk ? 'call shape compatible' : 'compat check failed'}
        </Badge>
        <span className="text-crt-gray">
          executeCall simulation:{' '}
          {rb.compat.ownerCallOk ? 'owner-signed OK' : 'owner-signed REVERTS'}
          {' / '}
          {rb.compat.strangerCallReverts
            ? 'non-owner reverts'
            : 'non-owner does NOT revert'}{' '}
          (checked {fmtTime(rb.compat.checkedAt)})
        </span>
      </div>
      <p className="text-xs text-crt-dim">
        read-only view of the real deployed StonkBrokers contracts on chain
        4663; our agent runs the identical call shape (ABI-parity selftest
        37/37).
      </p>
    </div>
  )
}

function GatewayStatus({
  health,
}: {
  health: { state: QueryState<HealthResponse>; refresh: () => void }
}) {
  if (health.state.kind === 'loading') {
    return <Badge tone="dim">gateway: checking…</Badge>
  }
  if (health.state.kind === 'error') {
    return (
      <Badge tone="neg" title={health.state.message}>
        gateway: unreachable
      </Badge>
    )
  }
  const h = health.state.data
  return (
    <div className="flex flex-wrap gap-2">
      <Badge tone={h.ok ? 'pos' : 'neg'}>
        gateway: {h.ok ? 'up' : 'down'}
      </Badge>
      <Badge tone={h.db ? 'pos' : 'neg'}>db: {h.db ? 'ok' : 'down'}</Badge>
      <Badge tone="dim">env: {h.environment}</Badge>
    </div>
  )
}

function NavTiles({
  state,
  performance,
}: {
  state: PortfolioState | null
  performance: PerformanceSummary | null
}) {
  if (!state) {
    return (
      <EmptyState
        title="no portfolio state"
        detail={`No NAV recorded yet — the agent has not completed a cycle. Chain history begins ${CHAIN_HISTORY_START}.`}
      />
    )
  }
  const positions = parsePositions(state.positions)
  // Day P&L = return between the last two NAV observations.
  const pts = performance?.points ?? []
  const last = pts[pts.length - 1]
  const prev = pts[pts.length - 2]
  const dayPct = last && prev ? last.nav / prev.nav - 1 : null
  const dayUsd = last && prev ? last.nav - prev.nav : null

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <StatTile
        label="nav"
        value={fmtUsd(state.navUsd)}
        sub={`as of ${fmtTime(state.asOf)}`}
      />
      <StatTile
        label="cash (usdg)"
        value={fmtUsd(positions.cashUsd)}
        valueClass="text-crt-gray"
      />
      <StatTile
        label="pnl / day"
        value={fmtSignedPct(dayPct)}
        sub={fmtUsd(dayUsd)}
        valueClass={signColor(dayPct)}
      />
      <StatTile
        label="return / total"
        value={fmtSignedPct(performance?.totalReturn ?? null)}
        sub={
          performance
            ? `max drawdown ${fmtSignedPct(performance.maxDrawdown)}`
            : undefined
        }
        valueClass={signColor(performance?.totalReturn ?? null)}
      />
    </div>
  )
}

function WeightsDonut({
  state,
  universe,
}: {
  state: PortfolioState
  universe: UniverseSnapshot | null
}) {
  const index = useMemo(() => buildTokenIndex(universe), [universe])
  const entries = Object.entries(state.weights).filter(([, w]) => w > 0)
  const weightSum = entries.reduce((a, [, w]) => a + w, 0)
  const cashWeight = Math.max(0, 1 - weightSum)
  const slices = [
    ...entries.map(([addr, w]) => ({ name: symbolFor(index, addr), value: w })),
    ...(cashWeight > 0.0005 ? [{ name: 'USDG', value: cashWeight }] : []),
  ]
  if (slices.length === 0) {
    return (
      <EmptyState
        title="no positions"
        detail="No holdings recorded yet — the agent has not traded."
      />
    )
  }
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            innerRadius="55%"
            outerRadius="85%"
            stroke="#111111"
            isAnimationActive={false}
          >
            {slices.map((s, i) => (
              <Cell
                key={s.name}
                fill={DONUT_COLORS[i % DONUT_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(v) => `${(Number(v) * 100).toFixed(1)}%`}
            contentStyle={{
              background: '#1c1c1c',
              border: '1px solid rgba(207,255,4,0.35)',
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

function PerfChart({ performance }: { performance: PerformanceSummary }) {
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={performance.points}>
          <CartesianGrid stroke="#474546" strokeDasharray="3 3" />
          <XAxis
            dataKey="t"
            stroke="#9e9e9e"
            tick={{ fontSize: 10 }}
            tickFormatter={(t: number) => fmtDate(t).slice(5)}
          />
          <YAxis
            stroke="#9e9e9e"
            tick={{ fontSize: 10 }}
            domain={['auto', 'auto']}
          />
          <Tooltip
            contentStyle={{
              background: '#1c1c1c',
              border: '1px solid rgba(207,255,4,0.35)',
              fontSize: 12,
            }}
            labelFormatter={(t) => fmtDate(Number(t))}
            formatter={(v: number | string, name: string) =>
              name === 'nav (usd)'
                ? fmtUsd(Number(v))
                : fmtSignedPct(Number(v))
            }
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="nav"
            name="nav (usd)"
            stroke="#cfff04"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
